import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "ismo_pwa_dismissed";
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (localStorage.getItem(DISMISS_KEY)) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari : pas d'événement natif → afficher l'aide au bout de qq secondes
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShow(true), 2500);
    }

    const onInstalled = () => {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const close = () => {
    setShow(false);
    setIosHelp(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {
        /* noop */
      }
      setDeferred(null);
      close();
    } else if (isIOS()) {
      setIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[9998] mx-auto max-w-md sm:left-auto sm:right-4 sm:mx-0"
      data-testid="pwa-install-banner"
    >
      <div className="pwa-slide-up overflow-hidden rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-white/10">
        <div className="flex items-center gap-3 p-3.5">
          <img src="/api/pwa/icon.png?size=192" alt="ISMO-TECH" className="h-11 w-11 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold leading-tight">Installer ISMO-TECH</p>
            <p className="text-[11px] leading-snug text-slate-300">
              Ajoutez l'app à votre écran d'accueil pour des alertes plus fiables et un accès instantané.
            </p>
          </div>
          <button
            data-testid="pwa-install-close"
            onClick={close}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {iosHelp ? (
          <div className="border-t border-white/10 bg-white/5 px-4 py-3 text-[12px] text-slate-200" data-testid="pwa-ios-help">
            <p className="flex items-center gap-1.5">
              1. Touchez <Share className="mx-0.5 inline h-4 w-4 text-sky-400" /> Partager
            </p>
            <p className="mt-1 flex items-center gap-1.5">
              2. Choisissez <Plus className="mx-0.5 inline h-4 w-4 text-sky-400" /> « Sur l'écran d'accueil »
            </p>
          </div>
        ) : (
          <div className="flex gap-2 border-t border-white/10 px-3.5 py-3">
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.02]"
            >
              <Download className="h-4 w-4" /> Installer l'application
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
